import { FC } from 'react';
import {
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Heading,
  Stack,
  useDisclosure,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import {
  useAppContext,
  useGifts,
  useParticipantProgress,
  useUpdateGoal,
} from '@frx/frx-react-context';
import { EditPencilSolid } from '../../../assets';

import {
  Card,
  DonationProgress,
  DonationStatistics,
  Modal,
  RecentDonations,
} from '../../../components';
import { ChangeGoal } from '../../../components/change-goal/ChangeGoal';

export const Donations: FC = () => {
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const { onClose, onOpen, isOpen } = useDisclosure();

  const {
    data: {
      authData: { user },
      eventData: { eventInfo, eventConfig },
    },
  } = useAppContext();

  const participantProgressResponse = useParticipantProgress({
    cons_id: user?.cons_id as number,
    fr_id: eventInfo?.id as number,
  });
  const participantProgress = participantProgressResponse?.data;
  const daysLeft = participantProgress?.daysLeft;
  const eventDate = eventInfo?.event_date;

  // show days left option if eventDate is today or in the future
  const showDaysLeft = () => eventDate && dayjs(eventDate).diff(dayjs()) > -1;

  const {
    mutate: updateGoal,
    isLoading,
    error,
    reset: resetQuery,
  } = useUpdateGoal();

  const giftsResponse = useGifts({
    cons_id: user?.cons_id as number,
    fr_id: eventInfo?.id as number,
    list_page_size: 5,
    list_sort_column: 'date_recorded',
    list_ascending: false,
  });
  const gifts = giftsResponse?.data?.gift;
  const totalGifts = giftsResponse?.data?.totalNumberResults as number;

  const handleEditGoalSave = (editGoal: number) =>
    updateGoal(editGoal, {
      onSuccess: () => onClose(),
    });

  const handleEditGoalCancel = () => {
    resetQuery();
    onClose();
  };

  return (
    <Card aria-label="Donations Column">
      <Heading size="md" color="brand.primary.swatch1">
        Donations
      </Heading>

      <Stack mt={4} spacing={8}>
        <VStack p={0} spacing={4}>
          <Flex width="full">
            <DonationProgress
              goal={Number(participantProgress?.personalProgress?.goal) || 0}
              raised={
                Number(participantProgress?.personalProgress?.raised) || 0
              }
            />
            <Flex alignItems="center">
              <CircularProgress
                capIsRound
                color={
                  Number(participantProgress?.personalProgress?.percent) > 99
                    ? 'brand.additional.success'
                    : 'brand.primary.swatch1'
                }
                size="4rem"
                thickness="1rem"
                value={participantProgress?.personalProgress?.percent}
              >
                <CircularProgressLabel fontSize="14px" fontWeight="bold">
                  {participantProgress?.personalProgress?.percent}%
                </CircularProgressLabel>
              </CircularProgress>
            </Flex>
          </Flex>

          <Modal
            isOpen={isOpen}
            onClose={handleEditGoalCancel}
            buttonProps={{
              colorScheme: 'blue',
              title: 'Edit Goal',
              variant: 'secondary',
              width: 'full',
              onClick: onOpen,
              leftIcon: <EditPencilSolid boxSize="16px" />,
            }}
            modalTitle="Edit Goal"
            size={isMobile ? 'xs' : 'md'}
          >
            <ChangeGoal
              errorMessage={error?.message as string}
              goalAmount={participantProgress?.personalProgress.goal}
              isLoading={isLoading}
              onCancel={handleEditGoalCancel}
              onSubmit={handleEditGoalSave}
            />
          </Modal>
        </VStack>
        <DonationStatistics
          totalDonations={totalGifts}
          {...(showDaysLeft() ? { daysLeft } : undefined)}
        />

        <RecentDonations
          headline="Recent Donations"
          gifts={gifts}
          totalGifts={totalGifts}
          eventName={eventInfo?.name || eventConfig?.publicName}
          pageUrl={user?.personalPageUrl || user?.customPersonalPageUrl}
        />
      </Stack>
    </Card>
  );
};
